import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import {Box, Center, Spinner, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import {hasItems} from '@amblique/king-living/app/utils/utils';

import {useBlurOverlay} from '@amblique/king-living/app/contexts/blur-overlay-context';
import {MegaMenuTrigger} from '@amblique/king-living/app/components/mega-menu/desktop/mega-menu-trigger';

const CLOSE_DELAY_MS = 100;

export const MegaMenuDesktop = ({isLoading = false, menuItems = [], navigationMenu, ...props}) => {
  const [activeMenuId, setActiveMenuId] = useState(null);
  const closeTimeoutRef = useRef(null);
  const isKeyboardOpenRef = useRef(false);
  const isMenuOpen = activeMenuId !== null;
  const styles = useMultiStyleConfig('MegaMenu', {isMenuOpen});
  const blurOverlay = useBlurOverlay();
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handleMenuOpen = useCallback((menuId) => {
    clearCloseTimeout();

    setActiveMenuId((current) => {
      // If opening a different menu item, reset keyboard flag
      if (current !== menuId) {
        isKeyboardOpenRef.current = false;
      }

      return menuId;
    });
  }, [clearCloseTimeout]);

  const handleMenuClose = useCallback(() => {
    // Don't close if opened via keyboard (unless explicitly called)
    if (isKeyboardOpenRef.current) {
      return;
    }
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      isKeyboardOpenRef.current = false; // Reset flag when closing
      setActiveMenuId(null);
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  // Force close (for Escape key or explicit close)
  const handleMenuForceClose = useCallback(() => {
    clearCloseTimeout();
    isKeyboardOpenRef.current = false; // Reset flag
    setActiveMenuId(null);
  }, [clearCloseTimeout]);

  // Sync blur overlay with menu state
  useEffect(() => {
    if (isMenuOpen) {
      blurOverlay.show({
        onClose: handleMenuClose,
        onForceClose: handleMenuForceClose,
      });
    } else {
      blurOverlay.hide();
    }
  }, [isMenuOpen, blurOverlay, handleMenuClose, handleMenuForceClose]);

  // Toggle menu open/close (for keyboard navigation)
  const handleMenuToggle = useCallback((menuId) => {
    clearCloseTimeout();

    setActiveMenuId((current) => {
      const willBeOpen = current !== menuId;

      isKeyboardOpenRef.current = willBeOpen; // Set flag if opening via keyboard

      return current === menuId ? null : menuId;
    });
  }, [clearCloseTimeout]);

  // Stable handlers that read menuId from data attribute
  const handleMenuOpenById = useCallback((event) => {
    const menuId = event.currentTarget.dataset.menuId;

    if (menuId) {
      handleMenuOpen(menuId);
    }
  }, [handleMenuOpen]);

  const handleMenuToggleById = useCallback((event) => {
    const menuId = event.currentTarget.dataset.menuId;

    if (menuId) {
      handleMenuToggle(menuId);
    }
  }, [handleMenuToggle]);

  if (isLoading) {
    return (
      <Box className="mega-menu-desktop-wrapper" position="relative" {...props} {...styles.megaMenuDesktopWrapper}>
        <Box as="nav" {...styles.megaMenuDesktopNav}>
          <Spinner color={styles.megaMenuLoadingSpinner?.color} size="sm" />
        </Box>
      </Box>
    );
  }

  if (!hasItems(menuItems)) {
    return null;
  }

  return (
    <Box className="mega-menu-desktop-wrapper" position="relative" {...props} {...styles.megaMenuDesktopWrapper}>
      <Box as="nav" onMouseLeave={handleMenuClose} {...styles.megaMenuDesktopNav}>
        {menuItems.map((menuItem) => (
          <MegaMenuTrigger
            key={menuItem.sys?.id}
            isOpen={activeMenuId === menuItem.sys?.id}
            menuId={menuItem.sys?.id}
            menuItem={menuItem}
            navigationMenu={navigationMenu}
            onClose={handleMenuClose}
            onForceClose={handleMenuForceClose}
            onOpen={handleMenuOpenById}
            onToggle={handleMenuToggleById}
          />
        ))}
      </Box>
      {/* Blur overlay is now controlled globally via BlurOverlayProvider */}
    </Box>
  );
};

MegaMenuDesktop.displayName = 'MegaMenuDesktop';
MegaMenuDesktop.propTypes = {
  isLoading: PropTypes.bool,
  menuItems: PropTypes.array,
  navigationMenu: PropTypes.object,
};
